import React, { useState, useEffect } from 'react';
import { Package, DollarSign, Clock, CheckCircle, Truck, XCircle, RefreshCw, Search, Plus, CreditCard, FileText, Mail, Phone, ChevronDown, ChevronUp, Eye, X, Send, Bell, AlertTriangle, Calendar, MapPin, MessageSquare, History } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PreOrdersTab = () => {
  const [preorders, setPreorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPreorder, setSelectedPreorder] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [expandedPreorder, setExpandedPreorder] = useState(null);
  const [detailModalPreorder, setDetailModalPreorder] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailType, setEmailType] = useState('reminder');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchPreorders();
  }, [filter]);

  const fetchPreorders = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API_URL}/api/preorders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreorders(res.data);
    } catch (error) {
      toast.error('Failed to load pre-orders');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (preorderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/preorders/${preorderId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPreorders();
      toast.success('Pre-order status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const recordPayment = async () => {
    if (!selectedPreorder || !paymentAmount) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/preorders/${selectedPreorder.id}/payment`, 
        { 
          amount: parseFloat(paymentAmount),
          notes: paymentNotes || 'Payment recorded',
          payment_type: 'payment'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPreorders();
      setShowPaymentModal(false);
      setSelectedPreorder(null);
      setPaymentAmount('');
      setPaymentNotes('');
      toast.success('Payment recorded successfully');
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  // Email templates
  const emailTemplates = {
    reminder: {
      label: 'Payment Reminder',
      icon: Bell,
      color: 'text-amber-600',
      getSubject: (po) => `Payment Reminder - Pre-Order ${po.order_code}`,
      getMessage: (po) => `Hi ${po.customer_name},\n\nThis is a friendly reminder that you have an outstanding balance of ${formatCurrency(po.balance_owing)} for your pre-order (${po.order_code}).\n\nProduct: ${po.product_name}\nTotal: ${formatCurrency(po.total_price)}\nPaid: ${formatCurrency(po.amount_paid)}\nBalance Due: ${formatCurrency(po.balance_owing)}\n\nPlease complete your payment to secure your order.\n\nThank you for choosing HomeLyph!\n\nBest regards,\nHomeLyph Team`
    },
    update: {
      label: 'Order Update',
      icon: MessageSquare,
      color: 'text-blue-600',
      getSubject: (po) => `Update on Your Pre-Order ${po.order_code}`,
      getMessage: (po) => `Hi ${po.customer_name},\n\nWe wanted to give you an update on your pre-order (${po.order_code}).\n\nProduct: ${po.product_name}\nCurrent Status: ${statusLabels[po.status]?.label || po.status}\n\n[Add your update message here]\n\nIf you have any questions, please don't hesitate to reach out.\n\nBest regards,\nHomeLyph Team`
    },
    ready: {
      label: 'Ready for Shipping',
      icon: Truck,
      color: 'text-purple-600',
      getSubject: (po) => `Your Pre-Order ${po.order_code} is Ready to Ship!`,
      getMessage: (po) => `Hi ${po.customer_name},\n\nGreat news! Your pre-order is ready to ship!\n\nOrder: ${po.order_code}\nProduct: ${po.product_name}\n\n${po.balance_owing > 0 ? `Please note: You have an outstanding balance of ${formatCurrency(po.balance_owing)}. Please complete your payment so we can ship your order.\n\n` : ''}We'll send you tracking information once your order ships.\n\nThank you for your patience!\n\nBest regards,\nHomeLyph Team`
    },
    shipped: {
      label: 'Shipping Confirmation',
      icon: Truck,
      color: 'text-indigo-600',
      getSubject: (po) => `Your Pre-Order ${po.order_code} Has Shipped!`,
      getMessage: (po) => `Hi ${po.customer_name},\n\nExciting news - your order has shipped!\n\nOrder: ${po.order_code}\nProduct: ${po.product_name}\nTracking Number: [Enter tracking number]\n\nYou can track your package at [carrier tracking URL].\n\nThank you for choosing HomeLyph!\n\nBest regards,\nHomeLyph Team`
    },
    custom: {
      label: 'Custom Email',
      icon: Mail,
      color: 'text-gray-600',
      getSubject: (po) => `Regarding Your Pre-Order ${po.order_code}`,
      getMessage: (po) => `Hi ${po.customer_name},\n\n[Your message here]\n\nBest regards,\nHomeLyph Team`
    }
  };

  const openEmailModal = (preorder, type = 'reminder') => {
    setSelectedPreorder(preorder);
    setEmailType(type);
    const template = emailTemplates[type];
    setEmailSubject(template.getSubject(preorder));
    setEmailMessage(template.getMessage(preorder));
    setShowEmailModal(true);
  };

  const sendEmail = async () => {
    if (!selectedPreorder || !emailSubject || !emailMessage) return;
    
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/preorders/${selectedPreorder.id}/email`, 
        { 
          email_type: emailType,
          subject: emailSubject,
          message: emailMessage,
          to_email: selectedPreorder.customer_email
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email sent to ${selectedPreorder.customer_email}`);
      setShowEmailModal(false);
      setSelectedPreorder(null);
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const statusLabels = {
    deposit_received: { label: 'Deposit Received', color: 'bg-amber-500', icon: CreditCard },
    partial_payment: { label: 'Partial Payment', color: 'bg-blue-500', icon: DollarSign },
    paid_in_full: { label: 'Paid in Full', color: 'bg-green-500', icon: CheckCircle },
    ready_to_ship: { label: 'Ready to Ship', color: 'bg-purple-500', icon: Package },
    shipped: { label: 'Shipped', color: 'bg-indigo-500', icon: Truck },
    delivered: { label: 'Delivered', color: 'bg-emerald-500', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: XCircle }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredPreorders = preorders.filter(po => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      po.order_code?.toLowerCase().includes(search) ||
      po.customer_name?.toLowerCase().includes(search) ||
      po.customer_email?.toLowerCase().includes(search) ||
      po.product_name?.toLowerCase().includes(search)
    );
  });

  // Calculate stats
  const stats = {
    total: preorders.length,
    pending_payment: preorders.filter(p => p.balance_owing > 0).length,
    ready_to_ship: preorders.filter(p => p.status === 'paid_in_full' || p.status === 'ready_to_ship').length,
    total_outstanding: preorders.reduce((sum, p) => sum + (p.balance_owing || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="preorders-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <Package className="h-8 w-8 text-[#003456]" />
            <span className="text-2xl font-bold">{stats.total}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Total Pre-Orders</p>
        </div>
        <div className="bg-white rounded-xl border border-amber-200 p-4">
          <div className="flex items-center justify-between">
            <DollarSign className="h-8 w-8 text-amber-500" />
            <span className="text-2xl font-bold text-amber-600">{stats.pending_payment}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Awaiting Payment</p>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-4">
          <div className="flex items-center justify-between">
            <Truck className="h-8 w-8 text-green-500" />
            <span className="text-2xl font-bold text-green-600">{stats.ready_to_ship}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Ready to Ship</p>
        </div>
        <div className="bg-white rounded-xl border border-blue-200 p-4">
          <div className="flex items-center justify-between">
            <CreditCard className="h-8 w-8 text-blue-500" />
            <span className="text-lg font-bold text-blue-600">{formatCurrency(stats.total_outstanding)}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Outstanding Balance</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
            data-testid="preorder-filter"
          >
            <option value="all">All Status</option>
            <option value="deposit_received">Deposit Received</option>
            <option value="partial_payment">Partial Payment</option>
            <option value="paid_in_full">Paid in Full</option>
            <option value="ready_to_ship">Ready to Ship</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by code, name, email..."
              className="pl-10 w-64"
              data-testid="preorder-search"
            />
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPreorders}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Pre-Orders Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-2 py-3 w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Order</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPreorders.map(preorder => {
                const StatusIcon = statusLabels[preorder.status]?.icon || Package;
                return (
                  <React.Fragment key={preorder.id}>
                    <tr 
                      className={`hover:bg-gray-50 cursor-pointer ${expandedPreorder === preorder.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setExpandedPreorder(expandedPreorder === preorder.id ? null : preorder.id)}
                      data-testid={`preorder-row-${preorder.order_code}`}
                    >
                      <td className="px-2 py-4 text-center">
                        {expandedPreorder === preorder.id ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-mono font-bold text-[#003456]">{preorder.order_code}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(preorder.created_at)}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{preorder.customer_name}</p>
                          <p className="text-sm text-gray-500">{preorder.customer_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="font-medium text-gray-900">{preorder.product_name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Total:</span>
                            <span className="font-mono">{formatCurrency(preorder.total_price)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium">
                            <span className="text-gray-700">Owing:</span>
                            <span className={`font-mono ${preorder.balance_owing > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                              {formatCurrency(preorder.balance_owing)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={preorder.status}
                          onChange={(e) => updateStatus(preorder.id, e.target.value)}
                          className={`text-xs font-medium px-2 py-1 rounded text-white cursor-pointer ${statusLabels[preorder.status]?.color || 'bg-gray-500'}`}
                          data-testid={`status-select-${preorder.order_code}`}
                        >
                          <option value="deposit_received">Deposit Received</option>
                          <option value="partial_payment">Partial Payment</option>
                          <option value="paid_in_full">Paid in Full</option>
                          <option value="ready_to_ship">Ready to Ship</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDetailModalPreorder(preorder)}
                            title="View Details"
                            data-testid={`view-preorder-${preorder.order_code}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEmailModal(preorder, preorder.balance_owing > 0 ? 'reminder' : 'update')}
                            title="Send Email"
                            className="text-blue-600"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          {preorder.balance_owing > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedPreorder(preorder);
                                setShowPaymentModal(true);
                              }}
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              data-testid={`record-payment-${preorder.order_code}`}
                            >
                              <Plus className="h-4 w-4 mr-1" /> Pay
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {expandedPreorder === preorder.id && (
                      <tr className="bg-blue-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="bg-white rounded-lg p-4 shadow-inner">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {/* Customer Info */}
                              <div>
                                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-blue-500" />
                                  Customer Details
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <p><span className="text-gray-500">Name:</span> <span className="font-medium">{preorder.customer_name}</span></p>
                                  <p><span className="text-gray-500">Email:</span> <a href={`mailto:${preorder.customer_email}`} className="text-[#003456] hover:underline">{preorder.customer_email}</a></p>
                                  {preorder.customer_phone && (
                                    <p><span className="text-gray-500">Phone:</span> <a href={`tel:${preorder.customer_phone}`} className="text-[#003456] hover:underline">{preorder.customer_phone}</a></p>
                                  )}
                                  {preorder.shipping_address && (
                                    <p><span className="text-gray-500">Address:</span> <span className="font-medium">{preorder.shipping_address}</span></p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Payment Info */}
                              <div>
                                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                  <DollarSign className="h-4 w-4 text-green-500" />
                                  Payment Details
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Total Price:</span>
                                    <span className="font-mono font-medium">{formatCurrency(preorder.total_price)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Deposit:</span>
                                    <span className="font-mono">{formatCurrency(preorder.deposit_amount)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Amount Paid:</span>
                                    <span className="font-mono text-green-600">{formatCurrency(preorder.amount_paid)}</span>
                                  </div>
                                  <div className="flex justify-between pt-2 border-t border-gray-200">
                                    <span className="font-medium">Balance Owing:</span>
                                    <span className={`font-mono font-bold ${preorder.balance_owing > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                      {formatCurrency(preorder.balance_owing)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Quick Actions */}
                              <div>
                                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                  <Send className="h-4 w-4 text-purple-500" />
                                  Quick Actions
                                </h4>
                                <div className="space-y-2">
                                  {preorder.balance_owing > 0 && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full justify-start text-amber-600 border-amber-300 hover:bg-amber-50"
                                      onClick={() => openEmailModal(preorder, 'reminder')}
                                    >
                                      <Bell className="h-4 w-4 mr-2" /> Send Payment Reminder
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full justify-start text-blue-600 border-blue-300 hover:bg-blue-50"
                                    onClick={() => openEmailModal(preorder, 'update')}
                                  >
                                    <MessageSquare className="h-4 w-4 mr-2" /> Send Order Update
                                  </Button>
                                  {preorder.status === 'paid_in_full' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full justify-start text-purple-600 border-purple-300 hover:bg-purple-50"
                                      onClick={() => openEmailModal(preorder, 'ready')}
                                    >
                                      <Truck className="h-4 w-4 mr-2" /> Send Ready to Ship Notice
                                    </Button>
                                  )}
                                  {preorder.status === 'shipped' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full justify-start text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                                      onClick={() => openEmailModal(preorder, 'shipped')}
                                    >
                                      <Truck className="h-4 w-4 mr-2" /> Send Shipping Confirmation
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full justify-start text-gray-600 border-gray-300 hover:bg-gray-50"
                                    onClick={() => openEmailModal(preorder, 'custom')}
                                  >
                                    <Mail className="h-4 w-4 mr-2" /> Send Custom Email
                                  </Button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Payment History */}
                            {preorder.payment_history && preorder.payment_history.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                  <History className="h-4 w-4 text-gray-500" />
                                  Payment History
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {preorder.payment_history.map((payment, idx) => (
                                    <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                                      <div className="flex justify-between items-center">
                                        <span className="font-mono font-medium text-green-600">
                                          +{formatCurrency(payment.amount)}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {formatDate(payment.date || payment.created_at)}
                                        </span>
                                      </div>
                                      {payment.notes && (
                                        <p className="text-xs text-gray-500 mt-1">{payment.notes}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Notes */}
                            {preorder.notes && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="font-semibold text-gray-700 mb-2">Notes</h4>
                                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{preorder.notes}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {filteredPreorders.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No pre-orders found</p>
              <p className="text-sm">Pre-orders will appear here when customers submit deposits.</p>
            </div>
          )}
        </div>
      </div>

      {/* Record Payment Modal */}
      {showPaymentModal && selectedPreorder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="payment-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold mb-4">Record Payment</h3>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Order</p>
                <p className="font-mono font-bold text-[#003456]">{selectedPreorder.order_code}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedPreorder.customer_name}</p>
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span>Balance Owing:</span>
                    <span className="font-mono font-bold text-amber-600">
                      {formatCurrency(selectedPreorder.balance_owing)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    min="0"
                    max={selectedPreorder.balance_owing}
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="pl-8"
                    placeholder="0.00"
                    data-testid="payment-amount-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setPaymentAmount(selectedPreorder.balance_owing.toString())}
                  className="text-xs text-[#003456] mt-1 hover:underline"
                >
                  Pay full balance ({formatCurrency(selectedPreorder.balance_owing)})
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <Input
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g., Final payment via credit card"
                  data-testid="payment-notes-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedPreorder(null);
                    setPaymentAmount('');
                    setPaymentNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={recordPayment}
                  disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
                  data-testid="confirm-payment-btn"
                >
                  Record Payment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedPreorder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="email-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
            {/* Header */}
            <div className="bg-[#003456] text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Send Email</h3>
                <p className="text-white/70 text-sm">To: {selectedPreorder.customer_email}</p>
              </div>
              <button 
                onClick={() => {
                  setShowEmailModal(false);
                  setSelectedPreorder(null);
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Email Type Selector */}
            <div className="p-4 border-b bg-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-2">Email Template:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(emailTemplates).map(([key, template]) => {
                  const TemplateIcon = template.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setEmailType(key);
                        setEmailSubject(template.getSubject(selectedPreorder));
                        setEmailMessage(template.getMessage(selectedPreorder));
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        emailType === key 
                          ? 'bg-[#003456] text-white' 
                          : 'bg-white border hover:bg-gray-100'
                      }`}
                    >
                      <TemplateIcon className="h-4 w-4" />
                      {template.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Email Form */}
            <div className="p-4 space-y-4 overflow-y-auto max-h-[50vh]">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                  data-testid="email-subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#003456] focus:border-[#003456]"
                  placeholder="Email message..."
                  data-testid="email-message"
                />
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowEmailModal(false);
                  setSelectedPreorder(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#003456] hover:bg-[#002040]"
                onClick={sendEmail}
                disabled={sendingEmail || !emailSubject || !emailMessage}
                data-testid="send-email-btn"
              >
                {sendingEmail ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModalPreorder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="preorder-detail-modal">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-xl">
            {/* Header */}
            <div className="bg-[#003456] text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Pre-Order Details</h3>
                <p className="text-white/70 text-sm font-mono">{detailModalPreorder.order_code}</p>
              </div>
              <button 
                onClick={() => setDetailModalPreorder(null)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Status Badge */}
              <div className="flex items-center justify-between mb-6">
                <span className={`px-4 py-2 rounded-full text-white font-medium ${statusLabels[detailModalPreorder.status]?.color || 'bg-gray-500'}`}>
                  {statusLabels[detailModalPreorder.status]?.label || detailModalPreorder.status}
                </span>
                <p className="text-sm text-gray-500">
                  Created: {new Date(detailModalPreorder.created_at).toLocaleString()}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-500" />
                    Customer Information
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Name</p>
                      <p className="text-lg font-semibold">{detailModalPreorder.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Email</p>
                      <a href={`mailto:${detailModalPreorder.customer_email}`} className="text-[#003456] hover:underline">
                        {detailModalPreorder.customer_email}
                      </a>
                    </div>
                    {detailModalPreorder.customer_phone && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Phone</p>
                        <a href={`tel:${detailModalPreorder.customer_phone}`} className="text-[#003456] hover:underline">
                          {detailModalPreorder.customer_phone}
                        </a>
                      </div>
                    )}
                    {detailModalPreorder.shipping_address && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Shipping Address</p>
                        <p className="text-gray-800">{detailModalPreorder.shipping_address}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Product Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-purple-500" />
                    Product Information
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Product</p>
                      <p className="text-lg font-semibold">{detailModalPreorder.product_name}</p>
                    </div>
                    {detailModalPreorder.product_id && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Product ID</p>
                        <p className="text-sm font-mono text-gray-600">{detailModalPreorder.product_id}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Payment Summary */}
              <div className="mt-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-500" />
                  Payment Summary
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-white/70 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Total Price</p>
                    <p className="text-xl font-bold font-mono">{formatCurrency(detailModalPreorder.total_price)}</p>
                  </div>
                  <div className="text-center p-3 bg-white/70 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Deposit</p>
                    <p className="text-xl font-bold font-mono text-blue-600">{formatCurrency(detailModalPreorder.deposit_amount)}</p>
                  </div>
                  <div className="text-center p-3 bg-white/70 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Total Paid</p>
                    <p className="text-xl font-bold font-mono text-green-600">{formatCurrency(detailModalPreorder.amount_paid)}</p>
                  </div>
                  <div className="text-center p-3 bg-white/70 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Balance Due</p>
                    <p className={`text-xl font-bold font-mono ${detailModalPreorder.balance_owing > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {formatCurrency(detailModalPreorder.balance_owing)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Payment History */}
              {detailModalPreorder.payment_history && detailModalPreorder.payment_history.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <History className="h-5 w-5 text-gray-500" />
                    Payment History
                  </h4>
                  <div className="space-y-2">
                    {detailModalPreorder.payment_history.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-mono font-medium text-green-600">+{formatCurrency(payment.amount)}</p>
                          {payment.notes && <p className="text-xs text-gray-500">{payment.notes}</p>}
                        </div>
                        <p className="text-sm text-gray-500">{formatDate(payment.date || payment.created_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {detailModalPreorder.notes && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-700 mb-2">Notes</h4>
                  <p className="text-gray-600 bg-gray-50 rounded-lg p-4">{detailModalPreorder.notes}</p>
                </div>
              )}
            </div>
            
            {/* Footer Actions */}
            <div className="p-4 border-t bg-gray-50 flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => window.open(`mailto:${detailModalPreorder.customer_email}`, '_blank')}
              >
                <Mail className="h-4 w-4 mr-2" /> Email Customer
              </Button>
              {detailModalPreorder.customer_phone && (
                <Button
                  variant="outline"
                  onClick={() => window.open(`tel:${detailModalPreorder.customer_phone}`, '_blank')}
                >
                  <Phone className="h-4 w-4 mr-2" /> Call Customer
                </Button>
              )}
              {detailModalPreorder.balance_owing > 0 && (
                <Button
                  variant="outline"
                  className="text-amber-600 border-amber-300"
                  onClick={() => {
                    setDetailModalPreorder(null);
                    openEmailModal(detailModalPreorder, 'reminder');
                  }}
                >
                  <Bell className="h-4 w-4 mr-2" /> Send Reminder
                </Button>
              )}
              <Button
                variant="outline"
                className="ml-auto"
                onClick={() => setDetailModalPreorder(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreOrdersTab;
